import { ButtonBase } from '@mui/material';
import { useDialog } from 'contexts/dialog';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormLabel,
    Icon,
    IconButton,
    MenuItem,
    Paper,
    ResponsiveDialog,
    Select,
    Stack,
    styled,
    Text,
    TextArea,
    TextInput,
} from 'shell/components';
import { getIconKind, getResourceName, getResourceType } from 'shell/components/ResourceChip';
import { CONTENT_BANK_RESOURCES_MB_LIMIT } from 'utils/constants';
import { bytesToMB, downloadFileFromURL, getS3FileURL, getWordCount } from 'utils/helper';

const RESOURCE_DESCRIPTION_WORD_COUNT_LIMIT = 200;

const RESOURCE_TYPES = [
    { 'course-syllabus': 'Course Syllabus' },
    { 'exemplar-paper': 'Exemplar Paper (High-Scoring)' },
    { 'grading-guidelines': 'Grading Guidelines' },
    { 'lecture-slides': 'Lecture Slides' },
    { 'reading-materials': 'Reading Materials' },
    { 'answer-key': 'Answer Key' },
    { other: 'Other' },
];

const EditContentBankResourcesDialog = memo(({ readOnly = false, open, resources, onClose, onSave }) => {
    const { setDialog, closeDialog } = useDialog();

    const [form, setForm] = useState(resources);
    const [errors, setErrors] = useState({});
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const validate = (resource) => {
        const getError = (resource, key) => {
            return {
                resource_type: !Boolean(resource.resource_type) ? 'Please select a resource type' : '',
                title: resource.resource_type === 'other' && !resource.title,
                description: getWordCount(resource.description) > RESOURCE_DESCRIPTION_WORD_COUNT_LIMIT,
            }[key];
        };

        const resourceTypeError = getError(resource, 'resource_type');
        const titleError = getError(resource, 'title');
        const descriptionError = getError(resource, 'description');

        setErrors((current) => ({
            ...current,
            [resource.id]: {
                resource_type: resourceTypeError,
                title: titleError,
                description: descriptionError,
            },
        }));

        return !Boolean(resourceTypeError) && !Boolean(titleError) && !Boolean(descriptionError);
    };

    const validateAll = () => {
        return form
            .filter(({ active }) => active)
            .map((resource) => {
                return validate(resource);
            })
            .reduce((acc, cur) => acc && cur, true);
    };

    const handleClose = () => {
        if (readOnly) return onClose();
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            setDialog({
                open: true,
                message: 'You Have Unsaved Changes',
                description: 'If you exit without saving, all unsaved changes will be lost.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Exit',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    closeDialog();
                    onSave(form);
                    onClose();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const handleClickDelete = useCallback(
        (targetId) => {
            setDialog({
                open: true,
                message: 'Delete File?',
                description: 'Are you sure you want to delete this file? This is permanent and cannot be undone.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    setForm((current) => {
                        return current.map((resource) => {
                            if (resource.id !== targetId) return resource;
                            return {
                                ...resource,
                                active: false,
                                modified_on: new Date().getTime(),
                            };
                        });
                    });
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        },
        [closeDialog, setDialog]
    );

    const handleChange = useCallback(
        (resourceId, key, value) => {
            setForm((current) => {
                return current.map((resource) => {
                    if (resource.id !== resourceId) return resource;
                    const newResource = {
                        ...resource,
                        [key]: value,
                    };
                    if (key === 'resource_type' && value !== 'other') {
                        delete newResource.title;
                    }
                    if (hasClickedSubmit) validate(newResource);
                    return newResource;
                });
            });
        },
        [hasClickedSubmit]
    );

    const handleClickSave = () => {
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            onSave(form);
            onClose();
        }
    };

    useEffect(() => {
        if (open) {
            setForm(
                resources.sort((a, b) => {
                    if (a.created_on === b.created_on) return 0;
                    return b.created_on - a.created_on;
                })
            );
        } else {
            setForm([]);
            setErrors({});
            setHasClickedSubmit(false);
        }
    }, [open, resources]);

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={3}>
                        <Text component={'h1'} variant={'h6'} weight={'medium'}>
                            Content Bank
                        </Text>
                        <Text color={'light'}>
                            [
                            {bytesToMB(
                                form.filter(({ active }) => active).reduce((acc, cur) => acc + cur.data.size, 0)
                            )}
                            /{CONTENT_BANK_RESOURCES_MB_LIMIT}MB] used
                        </Text>
                    </Stack>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={readOnly ? onClose : handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent>
                {form.filter(({ active }) => active).length ? (
                    <CustomPaper variant={'outlined'}>
                        {form
                            .filter(({ active }) => active)
                            .map((resource) => {
                                return (
                                    <MemoizedResource
                                        key={resource.id}
                                        resource={resource}
                                        errors={errors}
                                        handleChange={handleChange}
                                        handleClickDelete={handleClickDelete}
                                    />
                                );
                            })}
                    </CustomPaper>
                ) : (
                    <Text align={'center'} color={'light'} component={'i'}>
                        Your Content Bank is empty.
                    </Text>
                )}
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={readOnly ? onClose : handleClickSave}>{readOnly ? 'Close' : 'Save'}</Button>
            </DialogActions>
        </CustomResponsiveDialog>
    );
});

const MemoizedResource = memo(({ errors, readOnly, resource, handleChange, handleClickDelete }) => {
    const memoizedInputProps = useMemo(() => {
        return { readOnly };
    }, [readOnly]);

    const memoizedOnChange = useCallback(
        (e) => handleChange(resource.id, 'description', e.target.value),
        [handleChange, resource.id]
    );

    return (
        <ResourceStack key={resource.id} spacing={3}>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <CustomButtonBase onClick={() => downloadFileFromURL(getS3FileURL(resource.data))}>
                    <Stack alignItems={'center'} direction={'row'} spacing={3}>
                        <CustomIcon kind={getIconKind('file', resource)} />
                        <Stack alignItems={'flex-start'}>
                            <Text align={'left'}>{getResourceName('file', resource)}</Text>
                            <Text color={'light'}>{getResourceType('file', resource)}</Text>
                        </Stack>
                    </Stack>
                </CustomButtonBase>
                {!readOnly && (
                    <IconButton
                        aria-label={`delete ${getResourceName('file', resource)}`}
                        kind={'delete'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => handleClickDelete(resource.id)}
                    />
                )}
            </Stack>
            <Stack spacing={1}>
                <FormLabel>
                    Resource Type{' '}
                    <Text color={'error'} component={'span'}>
                        *
                    </Text>
                </FormLabel>
                <Select
                    error={Boolean(errors[resource.id]?.resource_type)}
                    inputProps={{
                        'aria-label': `select a resource type for ${resource.data.name}`,
                    }}
                    readOnly={readOnly}
                    required
                    value={resource.resource_type}
                    onChange={(e) => handleChange(resource.id, 'resource_type', e.target.value)}
                >
                    {RESOURCE_TYPES.map((item, index) => (
                        <MenuItem key={index} value={Object.keys(item)[0]}>
                            {Object.values(item)[0]}
                        </MenuItem>
                    ))}
                </Select>
                {Boolean(errors[resource.id]?.resource_type) && (
                    <Text color={'error'} variant={'p3'}>
                        {errors[resource.id].resource_type}
                    </Text>
                )}
            </Stack>
            {resource.resource_type === 'other' && (
                <TextInput
                    error={Boolean(errors[resource.id]?.title)}
                    label={'Please Specify'}
                    InputProps={{ readOnly }}
                    required
                    value={resource.title || ''}
                    onChange={(e) => handleChange(resource.id, 'title', e.target.value)}
                />
            )}
            <TextArea
                aria-label={`description for ${resource.data.name}`}
                error={Boolean(errors[resource.id]?.description)}
                helperText={`${getWordCount(resource.description)}/${RESOURCE_DESCRIPTION_WORD_COUNT_LIMIT} words`}
                helperTextPosition={'end'}
                InputProps={memoizedInputProps}
                label={'How should the AI use this information?'}
                placeholder={
                    'Ex. Use this exemplar paper as a baseline for grading. Analyze its structure, content, and quality to set a standard for high-scoring work.'
                }
                rows={6}
                value={resource.description}
                onChange={memoizedOnChange}
            />
        </ResourceStack>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            maxWidth: '700px',
            width: '100%',
            height: '100%',
        },
    };
});

const CustomPaper = styled(Paper)(() => {
    return {
        padding: 0,
    };
});

const ResourceStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(5),
        '&:nth-child(odd)': {
            background: theme.palette.ensightful.neutral[50],
        },
        '&:nth-child(even)': {
            background: theme.palette.ensightful.neutral[100],
        },
        '&:first-child': {
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
        },
        '&:last-child': {
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
        },
    };
});

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        '&.Mui-focusVisible': {
            borderRadius: '6px',
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomIcon = styled(Icon)(() => {
    return {
        fontSize: '2rem',
    };
});

export default EditContentBankResourcesDialog;
